import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, DownloadSimple, File as FileIcon, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const DOC_KINDS = [
  { value: "draft", label: "Draft copy", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "final_federal", label: "Final (Federal)", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "final_state", label: "Final (State)", tone: "bg-sky-50 text-sky-700 border-sky-200" },
  { value: "irs_ack", label: "IRS Acknowledgement", tone: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  { value: "supporting", label: "Supporting", tone: "bg-slate-100 text-slate-700 border-slate-200" },
];
const KIND_META = Object.fromEntries(DOC_KINDS.map(k => [k.value, k]));

export default function ReturnFilesPanel({ returnRow, onChange }) {
  const { user } = useAuth();
  const canUpload = ["admin", "cpa", "document_team"].includes(user?.role);
  const canDelete = ["admin", "cpa"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [kind, setKind] = useState("draft");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!returnRow?.id) return;
    const { data } = await api.get("/documents");
    setDocs((data || []).filter(d => d.return_id === returnRow.id));
  }, [returnRow?.id]);
  useEffect(() => { load(); }, [load]);

  const onFiles = async (files) => {
    if (!files || !files.length || !returnRow?.id) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("category", KIND_META[kind]?.label || "Return");
        fd.append("return_id", returnRow.id);
        fd.append("doc_kind", kind);
        await axios.post(`${API_BASE}/documents`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} file(s) uploaded to this return`);
      load(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API_BASE}/documents/${doc.id}/download`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Remove "${doc.filename}"?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const grouped = DOC_KINDS.map(k => ({ ...k, files: docs.filter(d => (d.doc_kind || "supporting") === k.value) }));

  return (
    <div className="space-y-4" data-testid="return-files-panel">
      <div className="flex items-baseline justify-between">
        <div className="text-xs font-mono uppercase tracking-widest text-[#D32F2F]">Return Files</div>
        <span className="text-xs font-mono text-slate-500">{docs.length} file{docs.length === 1 ? "" : "s"}</span>
      </div>

      {canUpload && (
        <div className="rounded-md border tf-hairline bg-slate-50 p-3 flex flex-wrap items-center gap-2">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="w-44 h-9" data-testid="return-file-kind"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_KINDS.map(k => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" onClick={() => inputRef.current?.click()} className="bg-[#0A2540] hover:bg-[#0A2540]/90" disabled={uploading} data-testid="return-file-upload-btn">
            <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload"}
          </Button>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
          <div className="text-[11px] text-slate-500 ml-auto">Client will see everything you upload here.</div>
        </div>
      )}

      <div className="space-y-3">
        {grouped.map(k => (
          <div key={k.value} className="rounded-md border tf-hairline bg-white overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b tf-hairline">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${k.tone}`}>{k.label}</span>
              </div>
              <span className="text-[10px] font-mono text-slate-500">{k.files.length}</span>
            </div>
            {k.files.length === 0 ? (
              <div className="p-3 text-xs text-slate-400 italic">Nothing uploaded in this bucket yet.</div>
            ) : (
              <ul className="divide-y tf-hairline">
                {k.files.map(f => (
                  <li key={f.id} className="px-3 py-2 flex items-center gap-2 hover:bg-slate-50/70" data-testid={`return-file-${f.id}`}>
                    <FileIcon size={14} className="text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm truncate text-[#0A2540]">{f.filename}</div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                        {(f.size / 1024).toFixed(1)} KB · {(f.uploaded_at || "").split("T")[0]}
                        {f.uploaded_by && <> · by {f.uploaded_by}</>}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => download(f)} data-testid={`return-file-download-${f.id}`}>
                      <DownloadSimple size={13} className="text-[#0A2540]" />
                    </Button>
                    {canDelete && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(f)} data-testid={`return-file-delete-${f.id}`}>
                        <Trash size={13} className="text-[#D32F2F]" />
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
